import { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  Eye,
  Printer,
  X,
  Package,
  User,
  MapPin,
  Phone,
  Search,
  Clock,
  CheckCircle2,
  Truck,
  Circle,
  Mail,
  RefreshCw,
  ArrowRight,
  PlayCircle,
  Check,
  Calendar,
  Filter
} from "lucide-react";
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
import OrderBook from './orderbook/OrderBook';
import { ShoppingBag, BookOpen } from "lucide-react";

const OrdersPage = () => {
  const [activeTab, setActiveTab] = useState('website');

  return (
    <Base Content={
      <div className="min-h-screen">
        {/* Tab Header */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl mb-6 max-w-md">
          <button
            onClick={() => setActiveTab('website')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'website'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            Website Orders
          </button>
          <button
            onClick={() => setActiveTab('orderbook')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'orderbook'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Order Book
          </button>
        </div>

        {activeTab === 'website' ? <WebsiteOrders /> : <OrderBook />}
      </div>
    } />
  );
};

const WebsiteOrders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceStatuses, setInvoiceStatuses] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingId, setTrackingId] = useState('');
  const [serviceProvider, setServiceProvider] = useState('');
  const [pendingOrderId, setPendingOrderId] = useState(null);

  const { authTokens } = useContext(AuthContext);

  const authAxios = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${authTokens?.access}`,
    },
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!authTokens?.access) {
        setError('Please login to view orders');
        setLoading(false);
        return;
      }

      const response = await authAxios.get('/api/orders/');
      const ordersArray = response.data?.orders || [];
      const productsArray = response.data?.products || [];

      setOrders(ordersArray);
      setProducts(productsArray);

      ordersArray.forEach(order => {
        if (order.status?.delivered_order && order.order_id) {
          fetchInvoiceStatus(order.order_id);
        }
      });
    } catch (err) {
      devLog.error('Error fetching orders:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceStatus = async (orderId) => {
    try {
      const response = await authAxios.get(`/api/orders/${orderId}/invoice-status/`);
      if (response.data.success) {
        setInvoiceStatuses(prev => ({
          ...prev,
          [orderId]: response.data.data
        }));
      }
    } catch (error) {
      devLog.error('Error fetching invoice status:', error);
    }
  };

  useEffect(() => {
    if (authTokens?.access) {
      fetchOrders();
    } else {
      setLoading(false);
      setError('Please login to view orders');
    }
  }, [authTokens]);

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.productname?.toLowerCase().includes(query) ||
        order.order_id?.toLowerCase().includes(query) ||
        order.name?.toLowerCase().includes(query) ||
        order.phonenumber?.includes(query)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(order => {
        const status = order.status;
        switch (statusFilter) {
          case 'pending': return !status?.started_order;
          case 'in_progress': return status?.started_order && !status?.finished_order;
          case 'ready': return status?.finished_order && !status?.delivered_order;
          case 'delivered': return status?.delivered_order;
          default: return true;
        }
      });
    }

    // Date filtering
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      result = result.filter(order => {
        const orderDate = order.order_created_at ? new Date(order.order_created_at) : null;
        if (!orderDate) return false;

        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return orderDate >= monthAgo;
          }
          case 'custom':
            if (dateFrom && dateTo) {
              const from = new Date(dateFrom);
              const to = new Date(dateTo);
              to.setHours(23, 59, 59, 999);
              return orderDate >= from && orderDate <= to;
            }
            if (dateFrom) return orderDate >= new Date(dateFrom);
            if (dateTo) {
              const to = new Date(dateTo);
              to.setHours(23, 59, 59, 999);
              return orderDate <= to;
            }
            return true;
          default:
            return true;
        }
      });
    }

    return result;
  }, [orders, searchQuery, statusFilter, dateFilter, dateFrom, dateTo]);

  const stats = useMemo(() => ({
    total: orders.length,
    pending: orders.filter(o => !o.status?.started_order).length,
    inProgress: orders.filter(o => o.status?.started_order && !o.status?.finished_order).length,
    ready: orders.filter(o => o.status?.finished_order && !o.status?.delivered_order).length,
    delivered: orders.filter(o => o.status?.delivered_order).length,
  }), [orders]);

  const getOrderProgress = (order) => {
    if (order.status?.delivered_order) return { step: 4, label: 'Delivered', color: 'emerald' };
    if (order.status?.finished_order) return { step: 3, label: 'Ready to Ship', color: 'blue' };
    if (order.status?.started_order) return { step: 2, label: 'In Progress', color: 'amber' };
    return { step: 1, label: 'New Order', color: 'orange' };
  };

  const handleExportCSV = () => {
    const headers = ['Order ID', 'Product', 'Customer', 'Phone', 'City', 'Price', 'Qty', 'Status', 'Date'];
    const rows = filteredOrders.map(o => {
      const progress = getOrderProgress(o);
      const date = o.order_created_at ? new Date(o.order_created_at).toLocaleDateString('en-IN') : '';
      return [o.order_id, o.productname, o.name, o.phonenumber, o.city, o.finalprice, o.quantity, progress.label, date];
    });
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStatusUpdate = async (orderId, statusType) => {
    // Intercept "Mark as Ready" to show tracking modal
    if (statusType === 'finished') {
      setPendingOrderId(orderId);
      setTrackingId('');
      setServiceProvider('');
      setShowTrackingModal(true);
      return;
    }

    await processStatusUpdate(orderId, statusType);
  };

  const handleTrackingConfirm = async () => {
    if (!trackingId.trim() || !serviceProvider.trim()) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please fill in both Tracking ID and Service Provider',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    setShowTrackingModal(false);
    await processStatusUpdate(pendingOrderId, 'finished', {
      tracking_id: trackingId.trim(),
      service_provider: serviceProvider.trim(),
    });
    setPendingOrderId(null);
    setTrackingId('');
    setServiceProvider('');
  };

  const processStatusUpdate = async (orderId, statusType, extraData = {}) => {
    setUpdatingStatus(`${orderId}-${statusType}`);

    const order = orders.find(o => o.order_id === orderId);
    if (!order) return;

    try {
      let dataToSend = {
        started_order: order.status?.started_order ? true : false,
        finished_order: order.status?.finished_order ? true : false,
        delivered_order: order.status?.delivered_order ? true : false,
        ...extraData,
      };

      // Set the status
      if (statusType === 'started') dataToSend.started_order = true;
      if (statusType === 'finished') {
        dataToSend.started_order = true;
        dataToSend.finished_order = true;
      }
      if (statusType === 'delivered') {
        dataToSend.started_order = true;
        dataToSend.finished_order = true;
        dataToSend.delivered_order = true;
      }

      await authAxios.post(`/api/orders/${orderId}/update/`, dataToSend);

      // Update local state
      setOrders(prev => prev.map(o => {
        if (o.order_id === orderId) {
          return {
            ...o,
            status: {
              ...o.status,
              started_order: dataToSend.started_order ? (o.status?.started_order || new Date().toISOString()) : null,
              finished_order: dataToSend.finished_order ? (o.status?.finished_order || new Date().toISOString()) : null,
              delivered_order: dataToSend.delivered_order ? (o.status?.delivered_order || new Date().toISOString()) : null,
              ...(extraData.tracking_id ? { tracking_id: extraData.tracking_id } : {}),
              ...(extraData.service_provider ? { service_provider: extraData.service_provider } : {}),
            }
          };
        }
        return o;
      }));

      if (dataToSend.delivered_order) {
        fetchInvoiceStatus(orderId);
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Status Updated Successfully!',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      devLog.error('Error updating status:', error);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Failed to update status',
        showConfirmButton: false,
        timer: 2000,
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleGenerateInvoice = async (orderId) => {
    const invoiceData = invoiceStatuses[orderId];
    const hasGstin = invoiceData?.designer_has_gstin;

    const { value: invoiceType } = await Swal.fire({
      title: 'Generate Designer Invoice',
      html: '<p style="color:#666;margin-bottom:4px;">Customer invoices are auto-sent at order time.<br/>Select a designer invoice to generate:</p>',
      input: 'select',
      inputOptions: hasGstin ? {
        'designer_normal': 'Designer Earnings Statement',
        'designer_gst': 'Designer GST Earnings Statement',
      } : {
        'designer_normal': 'Designer Earnings Statement',
      },
      inputPlaceholder: 'Select invoice type',
      showCancelButton: true,
      confirmButtonText: 'Generate',
      confirmButtonColor: '#000',
      inputValidator: (value) => {
        if (!value) return 'Please select an invoice type';
      },
    });

    if (invoiceType) {
      await generateInvoiceWithType(orderId, invoiceType);
    }
  };

  const INVOICE_TYPE_LABELS = {
    customer_normal: 'Customer Invoice',
    customer_gst: 'Customer GST Invoice',
    designer_normal: 'Designer Earnings',
    designer_gst: 'Designer GST Earnings',
  };

  const generateInvoiceWithType = async (orderId, invoiceType) => {
    try {
      Swal.fire({
        title: 'Generating Invoice...',
        text: `Creating ${INVOICE_TYPE_LABELS[invoiceType] || 'Invoice'}`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await authAxios.post(`/api/orders/${orderId}/generate-invoice/`, {
        invoice_type: invoiceType,
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Invoice Generated!',
          text: `Your ${INVOICE_TYPE_LABELS[invoiceType] || 'invoice'} is ready`,
          confirmButtonColor: '#000',
        });
        fetchInvoiceStatus(orderId);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to generate invoice',
        confirmButtonColor: '#000',
      });
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/download/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Download failed', confirmButtonColor: '#000' });
    }
  };

  const handlePreviewInvoice = async (invoiceId) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/preview/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Preview failed', confirmButtonColor: '#000' });
    }
  };

  const handlePrintInvoice = async (invoiceId) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/preview/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Print failed', confirmButtonColor: '#000' });
    }
  };

  const handleEmailInvoice = async (invoiceId, invoiceNumber) => {
    const result = await Swal.fire({
      title: 'Send Invoice via Email',
      text: `Send invoice ${invoiceNumber} to customer?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Send Email',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#000',
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: 'Sending Email...',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        });
        await authAxios.post(`/api/invoices/${invoiceId}/send-email/`);
        Swal.fire({
          icon: 'success',
          title: 'Email Sent!',
          text: 'Invoice has been sent to customer',
          confirmButtonColor: '#000',
        });
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Failed to send email', confirmButtonColor: '#000' });
      }
    }
  };

  const handleDownloadAlternate = async (invoiceId, invoiceNumber, targetType) => {
    try {
      const response = await authAxios.get(`/api/invoices/${invoiceId}/download-alternate/?type=${targetType}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}-${targetType}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Download failed',
        text: error.response?.data?.message || 'Failed to download invoice',
        confirmButtonColor: '#000',
      });
    }
  };

  // Status Step Component - Shows clickable steps to update status
  const StatusUpdateSection = ({ order }) => {
    const steps = [
      {
        id: 'started',
        key: 'started_order',
        label: 'Start Working',
        activeLabel: 'Started',
        description: 'Begin working on this order',
        icon: PlayCircle,
        color: 'amber'
      },
      {
        id: 'finished',
        key: 'finished_order',
        label: 'Mark as Ready',
        activeLabel: 'Ready',
        description: 'Order is ready to ship',
        icon: Package,
        color: 'blue'
      },
      {
        id: 'delivered',
        key: 'delivered_order',
        label: 'Mark Delivered',
        activeLabel: 'Delivered',
        description: 'Order has been delivered',
        icon: Truck,
        color: 'emerald'
      },
    ];

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5">
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Update Order Status</h3>
        <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-5">Tap each step as you complete it:</p>

        <div className="space-y-2 sm:space-y-3">
          {steps.map((step, index) => {
            const isCompleted = !!order.status?.[step.key];
            const isUpdating = updatingStatus === `${order.order_id}-${step.id}`;
            const Icon = step.icon;
            const prevStepCompleted = index === 0 || !!order.status?.[steps[index - 1].key];
            const canClick = !isCompleted && prevStepCompleted;

            return (
              <div
                key={step.id}
                className={`
                  flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all active:scale-[0.98]
                  ${isCompleted
                    ? `bg-${step.color}-50 border-${step.color}-200`
                    : canClick
                      ? 'bg-white border-gray-200 hover:border-gray-300 cursor-pointer hover:shadow-sm'
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }
                `}
                onClick={() => canClick && handleStatusUpdate(order.order_id, step.id)}
              >
                {/* Step Icon/Checkbox */}
                <div className={`
                  w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0
                  ${isCompleted
                    ? `bg-${step.color}-500 text-white`
                    : canClick
                      ? 'bg-gray-100 text-gray-400'
                      : 'bg-gray-100 text-gray-300'
                  }
                `}>
                  {isUpdating ? (
                    <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  ) : isCompleted ? (
                    <Check className="w-5 h-5 sm:w-6 sm:h-6" />
                  ) : (
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </div>

                {/* Step Info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm sm:text-base ${isCompleted ? `text-${step.color}-700` : 'text-gray-900'}`}>
                    {isCompleted ? step.activeLabel : step.label}
                  </p>
                  {isCompleted && order.status?.[step.key] ? (
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                      {new Date(order.status[step.key]).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  ) : (
                    <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{step.description}</p>
                  )}
                </div>

                {/* Action Indicator */}
                {!isCompleted && canClick && (
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs sm:text-sm font-medium">
                      <ArrowRight className="w-3.5 h-3.5 sm:hidden" />
                      <span className="hidden sm:inline">Click to Update</span>
                      <span className="sm:hidden">Update</span>
                    </span>
                  </div>
                )}
                {isCompleted && (
                  <div className="flex-shrink-0">
                    <CheckCircle2 className={`w-5 h-5 sm:w-6 sm:h-6 text-${step.color}-500`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Unable to Load Orders</h2>
          <p className="text-gray-500 text-base mb-6">{error}</p>
          <button
            onClick={fetchOrders}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl text-base font-medium hover:bg-gray-800"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Orders</h1>
            <p className="text-gray-500 text-sm sm:text-base mt-1">Manage and track all your customer orders</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white border-b sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 -mb-px">
              {[
                { id: 'all', label: 'All Orders', count: stats.total, color: 'gray' },
                { id: 'pending', label: 'New', count: stats.pending, color: 'orange' },
                { id: 'in_progress', label: 'In Progress', count: stats.inProgress, color: 'amber' },
                { id: 'ready', label: 'Ready to Ship', count: stats.ready, color: 'blue' },
                { id: 'delivered', label: 'Delivered', count: stats.delivered, color: 'emerald' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                    ${statusFilter === tab.id
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {tab.label}
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-semibold
                    ${statusFilter === tab.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}
                  `}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search & Date Filter */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by product name, customer name, phone number, or order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Date Filter + Export */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-1">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">Date:</span>
            </div>
            {[
              { id: 'all', label: 'All Time' },
              { id: 'today', label: 'Today' },
              { id: 'week', label: 'This Week' },
              { id: 'month', label: 'This Month' },
              { id: 'custom', label: 'Custom Range' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => { setDateFilter(opt.id); if (opt.id !== 'custom') { setDateFrom(''); setDateTo(''); } }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  dateFilter === opt.id
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              </div>
            )}
            <div className="w-full sm:w-auto sm:ml-auto">
              <button
                onClick={handleExportCSV}
                disabled={filteredOrders.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-3 border-gray-900 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-500 text-base">Loading your orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all' ? 'No orders found' : 'No orders yet'}
              </h3>
              <p className="text-gray-500 text-base mb-4">
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all'
                  ? 'Try a different search term or filter'
                  : 'When customers place orders, they will appear here'}
              </p>
              {(searchQuery || statusFilter !== 'all' || dateFilter !== 'all') && (
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter('all'); setDateFilter('all'); setDateFrom(''); setDateTo(''); }}
                  className="text-base text-gray-900 underline hover:no-underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order, index) => {
                const product = products.find(p => p.id === order.productid);
                const isExpanded = expandedOrder === order.order_id;
                const progress = getOrderProgress(order);
                const invoiceData = invoiceStatuses[order.order_id];

                return (
                  <div
                    key={order.order_id || index}
                    className={`bg-white rounded-2xl border-2 transition-all duration-200 overflow-hidden ${
                      isExpanded ? 'border-gray-300 shadow-lg' : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    {/* Order Header Row */}
                    <div
                      onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
                      className="flex items-start gap-3 sm:gap-4 p-3 sm:p-5 cursor-pointer active:bg-gray-50 transition-colors"
                    >
                      {/* Product Image */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={order.productimg1 || product?.img1}
                          alt={order.productname}
                          className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover bg-gray-100"
                        />
                        {progress.step === 1 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-white text-xs font-bold">!</span>
                          </div>
                        )}
                      </div>

                      {/* Order Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 sm:gap-4">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">{order.productname}</h3>
                            <p className="text-gray-500 text-sm sm:text-base mt-0.5 sm:mt-1 truncate">
                              <span className="font-medium">{order.name}</span>
                              <span className="mx-1.5 sm:mx-2">•</span>
                              <span>{order.city || 'N/A'}</span>
                            </p>
                            {order.order_created_at && (
                              <p className="text-gray-400 text-xs sm:text-sm mt-0.5 flex items-center gap-1.5">
                                <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                {new Date(order.order_created_at).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                })}
                                <span className="hidden sm:inline">
                                  {' at '}
                                  {new Date(order.order_created_at).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-gray-900 text-base sm:text-xl">₹{order.final_price || order.price}</p>
                            <p className="text-gray-400 text-xs sm:text-sm mt-0.5">Qty: {order.quantity || 1}</p>
                          </div>
                        </div>

                        {/* Status Badge & Expand Button */}
                        <div className="flex items-center justify-between mt-2.5 sm:mt-4">
                          <span className={`
                            inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium
                            bg-${progress.color}-50 text-${progress.color}-700
                          `}>
                            {progress.step === 1 && <Circle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            {progress.step === 2 && <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            {progress.step === 3 && <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            {progress.step === 4 && <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            {progress.label}
                          </span>

                          <button className="flex items-center gap-1.5 sm:gap-2 text-gray-500 hover:text-gray-700 font-medium text-xs sm:text-sm">
                            <span className="hidden sm:inline">{isExpanded ? 'Hide Details' : 'View Details'}</span>
                            <span className="sm:hidden">{isExpanded ? 'Hide' : 'Details'}</span>
                            {isExpanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t-2 border-gray-100 bg-gray-50 p-3 sm:p-5">
                        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                          {/* Left Column - Status Update */}
                          <StatusUpdateSection order={order} />

                          {/* Right Column - Details */}
                          <div className="space-y-5">
                            {/* Customer Details */}
                            <div className="bg-white rounded-xl border border-gray-200 p-5">
                              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <User className="w-5 h-5 text-gray-400" />
                                Customer Details
                              </h3>
                              <div className="space-y-3">
                                <p className="text-lg font-medium text-gray-900">{order.name}</p>
                                {order.customer_email && (
                                  <p className="flex items-center gap-3 text-gray-600">
                                    <Mail className="w-4 h-4 text-gray-400" />
                                    <a href={`mailto:${order.customer_email}`} className="hover:underline">{order.customer_email}</a>
                                  </p>
                                )}
                                <p className="flex items-center gap-3 text-gray-600">
                                  <Phone className="w-4 h-4 text-gray-400" />
                                  <a href={`tel:${order.phonenumber}`} className="hover:underline">{order.phonenumber}</a>
                                </p>
                                <p className="flex items-start gap-3 text-gray-600">
                                  <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                  <span>
                                    {order.address}{order.address2 && `, ${order.address2}`}<br />
                                    {order.city}, {order.state} - {order.pincode}
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Order Info */}
                            {(order.fabric || order.color || order.size || order.note) && (
                              <div className="bg-white rounded-xl border border-gray-200 p-5">
                                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                  <Package className="w-5 h-5 text-gray-400" />
                                  Order Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                  {order.fabric && (
                                    <div>
                                      <p className="text-sm text-gray-400">Fabric</p>
                                      <p className="font-medium text-gray-900">{order.fabric}</p>
                                    </div>
                                  )}
                                  {order.color && (
                                    <div>
                                      <p className="text-sm text-gray-400">Color</p>
                                      <p className="font-medium text-gray-900">{order.color}</p>
                                    </div>
                                  )}
                                  {order.size && (
                                    <div className="col-span-2">
                                      <p className="text-sm text-gray-400">Size / Measurements</p>
                                      <p className="font-medium text-gray-900">{order.size}</p>
                                    </div>
                                  )}
                                  {order.note && (
                                    <div className="col-span-2 pt-3 border-t border-gray-100">
                                      <p className="text-sm text-gray-400 mb-1">Customer Note</p>
                                      <p className="text-gray-700 italic">"{order.note}"</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Shipping Details - Show when tracking info exists */}
                            {(order.status?.tracking_id || order.status?.service_provider) && (
                              <div className="bg-white rounded-xl border border-gray-200 p-5">
                                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                  <Truck className="w-5 h-5 text-gray-400" />
                                  Shipping Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                  {order.status?.tracking_id && (
                                    <div>
                                      <p className="text-sm text-gray-400">Tracking ID</p>
                                      <p className="font-medium text-gray-900">{order.status.tracking_id}</p>
                                    </div>
                                  )}
                                  {order.status?.service_provider && (
                                    <div>
                                      <p className="text-sm text-gray-400">Service Provider</p>
                                      <p className="font-medium text-gray-900">{order.status.service_provider}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Invoice Section - Only show when delivered */}
                            {progress.step === 4 && (
                              <div className="bg-white rounded-xl border border-gray-200 p-5">
                                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                  <FileText className="w-5 h-5 text-gray-400" />
                                  Invoice
                                </h3>

                                {invoiceData?.has_invoice ? (
                                  <div className="space-y-4">
                                    <div className="bg-emerald-50 rounded-xl p-4 flex items-center gap-3">
                                      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                                        <CheckCircle2 className="w-5 h-5 text-white" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-emerald-700">
                                          {INVOICE_TYPE_LABELS[invoiceData.invoice.invoice_type] || 'Invoice'} Ready
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {invoiceData.invoice.invoice_number} • ₹{parseFloat(invoiceData.invoice.total_amount).toLocaleString('en-IN')}
                                          {invoiceData.invoice.invoice_type?.includes('designer') && (
                                            <span className="text-purple-600 font-medium"> → Net: ₹{parseFloat(invoiceData.invoice.net_amount).toLocaleString('en-IN')}</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      <button
                                        onClick={() => handlePrintInvoice(invoiceData.invoice.id)}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                                      >
                                        <Printer className="w-4 h-4" />
                                        Print
                                      </button>
                                      <button
                                        onClick={() => handlePreviewInvoice(invoiceData.invoice.id)}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                                      >
                                        <Eye className="w-4 h-4" />
                                        View
                                      </button>
                                      <button
                                        onClick={() => handleDownloadInvoice(invoiceData.invoice.id, invoiceData.invoice.invoice_number)}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-colors"
                                      >
                                        <Download className="w-4 h-4" />
                                        Download
                                      </button>
                                      <button
                                        onClick={() => handleEmailInvoice(invoiceData.invoice.id, invoiceData.invoice.invoice_number)}
                                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl font-medium transition-colors"
                                      >
                                        <Mail className="w-4 h-4" />
                                        Email
                                      </button>
                                    </div>

                                    {/* Download other designer invoice variants */}
                                    <div>
                                      <p className="text-xs text-gray-500 font-medium mb-2">Download other versions:</p>
                                      <div className="grid grid-cols-2 gap-2">
                                        {['designer_normal', 'designer_gst']
                                          .filter(t => {
                                            if (t === invoiceData.invoice.invoice_type) return false;
                                            if (t.includes('gst') && !invoiceData.designer_has_gstin) return false;
                                            return true;
                                          })
                                          .map(t => (
                                            <button
                                              key={t}
                                              onClick={() => handleDownloadAlternate(invoiceData.invoice.id, invoiceData.invoice.invoice_number, t)}
                                              className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 hover:border-gray-400 text-gray-600 hover:text-gray-800 rounded-lg text-xs font-medium transition-colors"
                                            >
                                              <Download className="w-3 h-3" />
                                              {INVOICE_TYPE_LABELS[t]}
                                            </button>
                                          ))
                                        }
                                      </div>
                                      <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" />
                                        Customer invoice auto-sent at order time
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleGenerateInvoice(order.order_id)}
                                    className="w-full py-4 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-base font-medium flex items-center justify-center gap-2 transition-colors"
                                  >
                                    <FileText className="w-5 h-5" />
                                    Generate Invoice
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Customization Images */}
                        {(order.fab_img || order.border_img || order.border_design_img) && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <h3 className="text-base font-semibold text-gray-900 mb-4">Customization Images</h3>
                            <div className="flex gap-4 overflow-x-auto pb-2">
                              {order.fab_img && (
                                <div className="flex-shrink-0">
                                  <img src={order.fab_img} alt="Fabric" className="w-24 h-24 rounded-xl object-cover border border-gray-200" />
                                  <p className="text-sm text-gray-500 text-center mt-2">Fabric</p>
                                </div>
                              )}
                              {order.border_img && (
                                <div className="flex-shrink-0">
                                  <img src={order.border_img} alt="Border" className="w-24 h-24 rounded-xl object-cover border border-gray-200" />
                                  <p className="text-sm text-gray-500 text-center mt-2">Border</p>
                                </div>
                              )}
                              {order.border_design_img && (
                                <div className="flex-shrink-0">
                                  <img src={order.border_design_img} alt="Design" className="w-24 h-24 rounded-xl object-cover border border-gray-200" />
                                  <p className="text-sm text-gray-500 text-center mt-2">Design</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Designer Notes */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            Internal Notes
                          </h3>
                          <textarea
                            defaultValue={order.status?.designer_note || ''}
                            placeholder="Add private notes about this order..."
                            rows={3}
                            className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                            onBlur={async (e) => {
                              const note = e.target.value;
                              if (note !== (order.status?.designer_note || '')) {
                                try {
                                  await authAxios.post(`/api/orders/${order.order_id}/update/`, { designer_note: note });
                                  const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 1500, timerProgressBar: true });
                                  Toast.fire({ icon: "success", title: "Note saved" });
                                } catch {
                                  Swal.fire({ title: 'Error', text: 'Failed to save note.', icon: 'error', confirmButtonColor: '#d45614' });
                                }
                              }
                            }}
                          />
                          <p className="text-xs text-gray-400 mt-1">Auto-saves when you click away</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tracking Details Modal */}
        {showTrackingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowTrackingModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-gray-600" />
                  Shipping Details
                </h2>
                <button
                  onClick={() => setShowTrackingModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-5">Enter the tracking details before marking this order as shipped.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Tracking ID <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder="Enter tracking number"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Provider <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={serviceProvider}
                    onChange={(e) => setServiceProvider(e.target.value)}
                    placeholder="e.g. Delhivery, BlueDart, India Post"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowTrackingModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl text-base font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTrackingConfirm}
                  className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl text-base font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  Confirm & Ship
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default OrdersPage;
